import React, { useEffect, useMemo } from 'react';
import { useSuccessToastContext } from 'contexts/SuccessToastContext';
import { useFormikContext } from 'formik';
import { createPortal } from 'react-dom';
import { Alert, Box, Toast } from '@indeed/ifl-components';
import { themed } from '@indeed/ifl-css';
import ThemeProvider from 'Components/ThemeProvider';
import { ALERT_TIMEOUT } from 'Constants/Alert';
import { style } from './Notifications.jss';
const HEADLINES = {
    resume: '履歴書のデータを保存しました。',
    profile: 'プロフィールデータを保存しました。',
    logout: 'Indeedアカウントからログアウトしました。'
};
const POSITIONS = {
    resume: '84px',
    profile: '72px',
    logout: '88px'
};
const StyledAlert = themed(Alert, {
    styleProps: ['hasDismiss'],
    shouldForwardProp: (prop) => prop !== 'hasDismiss'
})(() => style.alert);
export const StyledToast = themed(Toast)(() => ({
    alignItems: 'center',
    maxWidth: 'none',
    // `<StyledNotificationBody>`, unfortunately we don't have direct control to the style there
    'div + div > div': {
        flex: 1
    }
}));
function toToastStatus(status) {
    return status === 'ai' ? 'info' : status;
}
const ActionToastImpl = ({ shouldUseIFLToast, status, ...rest }) => {
    if (shouldUseIFLToast) {
        return React.createElement(StyledToast, { status: toToastStatus(status), type: "polite", ...rest });
    }
    return React.createElement(StyledAlert, { ...rest, status: status });
};
const SuccessAlertImpl = ({ alertType, onDismiss }) => (React.createElement(Box, { sx: { ...style.wrapper, bottom: POSITIONS[alertType] } },
    React.createElement(ActionToastImpl, { shouldUseIFLToast: 
        // `alertType="logout"` sticks, meaning, not working as a toast
        alertType !== 'logout', status: alertType === 'logout' ? 'info' : 'success', dismissProps: {
            'aria-label': '通知を閉じる'
        }, onDismiss: onDismiss }, HEADLINES[alertType])));
export const SuccessAlert = (props) => {
    const portalRoot = useMemo(() => {
        if (typeof document === 'undefined') {
            return null;
        }
        const elem = document.createElement('div');
        elem.setAttribute('role', 'complementary');
        document.body.insertBefore(elem, document.body.firstChild);
        return elem;
    }, []);
    return portalRoot ? (createPortal(React.createElement(ThemeProvider, null,
        React.createElement(SuccessAlertImpl, { ...props })), portalRoot)) : (React.createElement(SuccessAlertImpl, { ...props }));
};
export const OneGraphResumeSaveSuccessToast = () => {
    const { setIsSuccessToastVisible } = useSuccessToastContext();
    const onClose = () => setIsSuccessToastVisible(false);
    useEffect(() => {
        const timeout = setTimeout(() => {
            onClose();
        }, ALERT_TIMEOUT);
        return () => clearTimeout(timeout);
    }, [onClose]);
    return React.createElement(SuccessAlertImpl, { alertType: "profile", onDismiss: onClose });
};
export const ErrorToast = ({ alertType, status = 'critical', ...rest }) => {
    return (React.createElement(Box, { sx: [style.wrapper, { bottom: POSITIONS[alertType] }] },
        React.createElement(StyledToast, { ...rest, status: status, type: "polite" })));
};
export const OneGraphResumeSaveErrorToast = ({ onClose }) => {
    const { setSubmitting } = useFormikContext();
    useEffect(() => {
        setSubmitting(false); // Reset isSubimitting status when error toast is shown
        const timeout = setTimeout(() => {
            onClose?.();
        }, ALERT_TIMEOUT);
        return () => clearTimeout(timeout);
    }, [onClose]);
    return (React.createElement(ErrorToast, { alertType: "resume", onDismiss: onClose }, "\u4FDD\u5B58\u306B\u5931\u6557\u3057\u307E\u3057\u305F\u3002\u30A4\u30F3\u30BF\u30FC\u30CD\u30C3\u30C8\u63A5\u7D9A\u3092\u78BA\u8A8D\u306E\u4E0A\u3001\u3082\u3046\u4E00\u5EA6\u304A\u8A66\u3057\u304F\u3060\u3055\u3044\u3002"));
};
export default SuccessAlert;
